import { type Dispatch, type SetStateAction } from 'react';
import { enableAccessCheckFilterOnGetTeams } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import type { ResourceSpec } from '@gonfalon/maintainer/internal';
import type { UseMaintainerItemsParams } from '@gonfalon/maintainer/internal/useMaintainerItems';
import type { SelectMaintainerTagsProps } from '@gonfalon/maintainer/SelectMaintainerTags';
import { SelectMaintainerTags } from '@gonfalon/maintainer/SelectMaintainerTags';

import type { ApprovalRequestResourceKind } from '#utils/approvals/types';
import type { Member as ImmutableMember } from '#utils/members/records/Member';
import { createMember } from '#utils/members/records/Member';
import type { Team as ImmutableTeam } from '#utils/teams/records/Team';
import { createTeam } from '#utils/teams/records/Team';

import { useResourceSpec } from './SelectReviewersContainer';

export type ReviewersMap = Map<`team:${string}` | `member:${string}`, ImmutableMember | ImmutableTeam>;

export interface SelectReviewerTagProps
  extends
    Pick<UseMaintainerItemsParams, 'maintainerIds' | 'maintainerTeamKeys' | 'filterOutCurrentMember'>,
    Pick<
      SelectMaintainerTagsProps,
      'className' | 'onBlur' | 'onClick' | 'isRequired' | 'label' | 'description' | 'placement'
    > {
  enableFilterByApprovers?: boolean;
  resourceKind?: ApprovalRequestResourceKind;
  setReviewersMap: Dispatch<SetStateAction<ReviewersMap>>;
  resourceKey: string;
  onChange(values: Array<{ type: 'team' | 'member'; id: string }>): void;
}

export function SelectReviewerTag({
  resourceKind,
  enableFilterByApprovers = true,
  maintainerIds = [],
  maintainerTeamKeys = [],
  filterOutCurrentMember,
  resourceKey,
  className,
  isRequired,
  label = 'Request approval',
  onBlur,
  onClick,
  onChange,
  setReviewersMap,
  description,
  placement,
}: SelectReviewerTagProps) {
  const resourceSpec = useResourceSpec({
    enabled: enableFilterByApprovers,
    resourceKind,
    resourceKey,
  });
  const memberAccessCheck: ResourceSpec | undefined = resourceSpec
    ? `reviewApprovalRequest:${encodeURIComponent(resourceSpec)}`
    : undefined;
  const accessCheckFilterOnGetTeamsEnabled = enableAccessCheckFilterOnGetTeams();
  const accessChecks = {
    members: memberAccessCheck,
    teams:
      resourceSpec && accessCheckFilterOnGetTeamsEnabled
        ? { accessCheck: 'reviewApprovalRequest' as const, accessCheckResource: resourceSpec }
        : undefined,
  };

  return (
    <ErrorBoundary severity="medium" fallbackRender={() => <>Unable to load reviewers</>}>
      <SelectMaintainerTags
        id="notifyMembers"
        label={label}
        description={description}
        placeholder="Select reviewers"
        selectionMode="multiple"
        isRequired={isRequired}
        className={className}
        maintainerIds={maintainerIds}
        maintainerTeamKeys={maintainerTeamKeys}
        filterOutCurrentMember={filterOutCurrentMember}
        accessChecks={accessChecks}
        onBlur={onBlur}
        onSelectionChange={(selections) => {
          setReviewersMap((prev) => {
            selections?.forEach((selection) => {
              const prefix = selection.type === 'team' ? 'team' : 'member';

              if (prev.has(`${prefix}:${selection.id}`)) {
                return prev;
              }
              prev.set(
                `${prefix}:${selection.id}`,
                selection.type === 'team'
                  ? createTeam(
                      // @ts-expect-error - immutable record / openapi mismatch
                      selection.resource,
                    )
                  : createMember(
                      // @ts-expect-error - immutable record / openapi mismatch
                      selection.resource,
                    ),
              );
            });

            return prev;
          });

          onChange(selections.map((selection) => ({ id: selection.id, type: selection.type })));
        }}
        placement={placement}
        onClick={onClick}
      />
    </ErrorBoundary>
  );
}
